/**
 * Avatar Upload Controller
 *
 * End-to-end "change profile picture" flow, mirroring UDG Poker:
 *   1. @capacitor/camera picks an image from the gallery (native) or a file
 *      dialog (web/desktop) — `CameraSource.Photos` covers both.
 *   2. Croppie shows a circular crop UI.
 *   3. On save we export a square 256px JPEG and POST it to the main-server,
 *      which stores it on Bunny CDN and returns the public picture URL.
 *
 * Self-contained: owns its DOM overlay + Croppie instance and tears them down
 * on close. main.ts constructs one instance and calls `change()` from the
 * Settings `account:change-picture` event.
 */

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import Croppie from 'croppie';
import 'croppie/croppie.css';
import { uploadAvatar } from '../../api/avatarApi';
import { isAndroidNative } from '../../util/platform';

export interface AvatarUploadOptions {
  /** Where to mount the crop modal (typically the #ui-container). */
  container: HTMLElement;
  /** Returns the current JWT (from auth:success). Null if not authed yet. */
  getToken: () => string | null;
  /** Called with the new CDN picture URL after a successful upload. */
  onUploaded: (picture: string) => void;
}

const OUTPUT_SIZE = 256; // square px exported to the server

export class AvatarUploadController {
  private opts: AvatarUploadOptions;
  private overlay: HTMLElement | null = null;
  private croppie: Croppie | null = null;
  private busy = false;

  constructor(opts: AvatarUploadOptions) {
    this.opts = opts;
  }

  /** Entry point: pick a photo, then show the crop modal. */
  async change(): Promise<void> {
    if (this.busy) return;

    const token = this.opts.getToken();
    if (!token) {
      console.warn('[avatar] no auth token yet — skipping change-picture');
      return;
    }

    let dataUrl: string;
    let sourceName: string | undefined;
    try {
      if (isAndroidNative()) {
        // Android: use the system photo picker (FilePicker) so we DON'T need the
        // broad READ_MEDIA_IMAGES permission — Play's Photo & Video Permissions
        // policy requires the picker for one-off selection like a profile photo.
        // iOS/web keep the Capacitor camera path below (PHPicker / file input)
        // exactly as before — this branch is the only thing that changed.
        const res = await FilePicker.pickImages({ readData: true });
        const file = res.files?.[0];
        if (!file?.data) return;
        sourceName = file.name;
        dataUrl = `data:${file.mimeType || 'image/jpeg'};base64,${file.data}`;
      } else {
        const photo = await Camera.getPhoto({
          quality: 90,
          resultType: CameraResultType.Base64,
          source: CameraSource.Photos,
          // Hint the native picker toward a square-ish selection; Croppie does
          // the authoritative crop afterward.
          width: 1024,
          height: 1024,
        });
        if (!photo.base64String) return;
        const format = photo.format || 'jpeg';
        dataUrl = `data:image/${format};base64,${photo.base64String}`;
      }
    } catch (err) {
      // User cancelled the picker, or permission denied. Either way: no-op.
      return;
    }

    // Android's Chromium WebView can't decode HEIC/HEIF (Samsung phones shoot
    // HEIF, and the FilePicker hands back the raw file), so the cropper's <img>
    // renders a broken-image placeholder. Transcode HEIC → JPEG up front. No-op
    // for already-renderable formats; the converter is lazy-loaded so it only
    // ships when a HEIC is actually picked. iOS/WKWebView decodes HEIC natively.
    try {
      dataUrl = await this.toRenderableJpeg(dataUrl, sourceName);
    } catch (err) {
      console.warn('[avatar] HEIC→JPEG conversion failed:', err);
      return;
    }

    this.openCropper(dataUrl, token);
  }

  /** Convert a HEIC/HEIF data URL to a JPEG data URL the WebView can render.
   *  Returns the input unchanged for any other (already-decodable) format. */
  private async toRenderableJpeg(dataUrl: string, name?: string): Promise<string> {
    const isHeic =
      /^data:image\/(heic|heif)/i.test(dataUrl) || /\.(heic|heif)$/i.test(name ?? '');
    if (!isHeic) return dataUrl;

    const blob = await (await fetch(dataUrl)).blob();
    const { default: heic2any } = await import('heic2any');
    const out = await heic2any({ blob, toType: 'image/jpeg', quality: 0.9 });
    const jpeg = Array.isArray(out) ? out[0] : out;
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error('read failed'));
      reader.readAsDataURL(jpeg);
    });
  }

  private openCropper(imageDataUrl: string, token: string): void {
    this.closeOverlay(); // defensive: never stack two modals

    const overlay = document.createElement('div');
    overlay.className = 'avatar-crop-overlay';
    overlay.innerHTML = `
      <div class="avatar-crop-modal" role="dialog" aria-label="Crop photo">
        <div class="avatar-crop-title">CROP PHOTO</div>
        <div class="avatar-crop-stage"></div>
        <div class="avatar-crop-hint">Drag to position · pinch / scroll to zoom</div>
        <div class="avatar-crop-error" hidden></div>
        <div class="avatar-crop-actions">
          <button class="avatar-crop-btn avatar-crop-cancel" type="button">CANCEL</button>
          <button class="avatar-crop-btn avatar-crop-save" type="button">SAVE</button>
        </div>
      </div>
    `;
    this.opts.container.appendChild(overlay);
    this.overlay = overlay;

    const stage = overlay.querySelector<HTMLElement>('.avatar-crop-stage')!;
    const errEl = overlay.querySelector<HTMLElement>('.avatar-crop-error')!;
    const cancelBtn = overlay.querySelector<HTMLButtonElement>('.avatar-crop-cancel')!;
    const saveBtn = overlay.querySelector<HTMLButtonElement>('.avatar-crop-save')!;

    // Croppie measures its boundary at construction time, so the stage must be
    // laid out (it is — we appended the overlay above with fixed CSS sizes).
    this.croppie = new Croppie(stage, {
      viewport: { width: 220, height: 220, type: 'circle' },
      boundary: { width: 280, height: 280 },
      showZoomer: true,
      enableExif: true,
    });
    this.croppie.bind({ url: imageDataUrl });

    cancelBtn.addEventListener('click', () => this.closeOverlay());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay && !this.busy) this.closeOverlay();
    });

    saveBtn.addEventListener('click', async () => {
      if (this.busy || !this.croppie) return;
      this.setBusy(true, saveBtn, cancelBtn);
      errEl.hidden = true;
      try {
        // Square JPEG (circle:false) — display surfaces apply the circular
        // mask via CSS, and JPEG avoids transparent-corner artifacts.
        const cropped = await this.croppie.result({
          type: 'base64',
          size: { width: OUTPUT_SIZE, height: OUTPUT_SIZE },
          format: 'jpeg',
          quality: 0.85,
          circle: false,
        });
        const { picture } = await uploadAvatar(cropped, token);
        this.opts.onUploaded(picture);
        this.closeOverlay();
      } catch (err) {
        console.warn('[avatar] upload failed:', err);
        errEl.textContent = err instanceof Error ? err.message : 'Upload failed';
        errEl.hidden = false;
        this.setBusy(false, saveBtn, cancelBtn);
      }
    });
  }

  private setBusy(busy: boolean, saveBtn: HTMLButtonElement, cancelBtn: HTMLButtonElement): void {
    this.busy = busy;
    saveBtn.disabled = busy;
    cancelBtn.disabled = busy;
    saveBtn.textContent = busy ? 'SAVING…' : 'SAVE';
  }

  private closeOverlay(): void {
    if (this.croppie) {
      this.croppie.destroy();
      this.croppie = null;
    }
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    this.busy = false;
  }

  destroy(): void {
    this.closeOverlay();
  }
}
